/**
 * PropertyGroup HTTP-boundary Zod schemas — SCRUM-345 (DS-014 S3) step 10.
 *
 * Single source of truth for the PropertyGroup REST surface request bodies.
 * Consumed by the interface layer (PropertyGroupsController) via inline
 * `safeParse` (no NestJS validation pipe) — mirrors properties.controller.ts.
 *
 * Four schemas:
 *   - createPropertyGroupSchema — name (required, non-empty) + description (optional/nullable)
 *   - updatePropertyGroupSchema — name? (non-empty when present) + description? (nullable)
 *   - addGroupMembersSchema     — propertyIds: uuid[].min(1).max(500)
 *   - removeGroupMembersSchema  — propertyIds: uuid[].min(1).max(500)
 *
 * UUID-array shape mirrors set-property-parent.schema.ts:28-31 (z.string().uuid());
 * the `.max(500)` bulk cap mirrors pricing/baseline.ts:79 (BulkSetBaselineRequestSchema).
 * The interface layer's fail-all/no-partial-write and org-scoping guarantees are
 * enforced downstream by AddPropertiesToGroupUseCase / RemovePropertiesFromGroupUseCase.
 */
import { z } from 'zod';
/** Max number of properties accepted in a single add/remove-members batch. */
export const GROUP_MEMBER_BATCH_MAX = 500;
// ---------------------------------------------------------------------------
// createPropertyGroupSchema
// ---------------------------------------------------------------------------
export const createPropertyGroupSchema = z.object({
    /** Group display name — required, non-empty. */
    name: z.string().min(1),
    /** Optional free-text description; explicit null clears it. */
    description: z.string().nullable().optional(),
});
// ---------------------------------------------------------------------------
// updatePropertyGroupSchema
// ---------------------------------------------------------------------------
export const updatePropertyGroupSchema = z.object({
    /** New name — non-empty when present. Omit to leave unchanged. */
    name: z.string().min(1).optional(),
    /** New description; explicit null clears it. Omit to leave unchanged. */
    description: z.string().nullable().optional(),
});
// ---------------------------------------------------------------------------
// addGroupMembersSchema / removeGroupMembersSchema
// ---------------------------------------------------------------------------
const propertyIdListSchema = z.string().uuid().array().min(1).max(GROUP_MEMBER_BATCH_MAX);
export const addGroupMembersSchema = z.object({
    /** UUIDs of properties to add to the group (1..500). */
    propertyIds: propertyIdListSchema,
});
export const removeGroupMembersSchema = z.object({
    /** UUIDs of properties to remove from the group (1..500). */
    propertyIds: propertyIdListSchema,
});
//# sourceMappingURL=property-group.schema.js.map